import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import settings from '../../config/settings';
import ClientAnalytics from '../analytics/ClientAnalytics';
import {CardClickTypes} from '../analytics/AnalyticsConst';
import * as BrowserUtils from '../utils/BrowserUtils';
import EmbedVideo from './EmbedVideo';
import * as CardUtils from '../utils/CardUtils';
import {cardLoadMode, cardStyles} from '../const/VuCardConst';
import urlParse from 'url-parse';
import Cookies from 'js-cookie';
import * as RedirectConst from '../const/RedirectConst';
import FrameMessages from '../const/FrameMessages';
import * as CardDestinationUtils from '../utils/CardDestinationUtils';
import {injectHtml} from '../utils/DomUtils';
import {getImageUrl} from '../utils/ApiUtils';
import DestinationTypes from '../const/DestinationTypes';

export default class VuCardClassic extends React.Component {
  constructor(props) {
    super(props);

    this.providerTrackingCodeContainer = null;
    this.state = {
      isMobile: false,
      device: '',
      destinations: []
    };

    this.callToActionContainer = null;
    this.getTwitterShareUrl = this.getTwitterShareUrl.bind(this);
    this.getFbShareUrl = this.getFbShareUrl.bind(this);
    this.getGooglePlusShareUrl = this.getGooglePlusShareUrl.bind(this);
    this.getLinkedinShareUrl = this.getLinkedinShareUrl.bind(this);
    this.onShareClick = this.onShareClick.bind(this);
    this.getMediaConfig = this.getMediaConfig.bind(this);
  }

  componentDidMount() {
    const {record} = this.props;
    const device = BrowserUtils.getDeviceType();
    const destinations = this.getDestinationConfigs(record, device);

    this.setState({
      isMobile: BrowserUtils.isMobile.any(),
      device,
      destinations
    });

    this.sendFrameRedirectCookieToParentWindow();
    this.setupDestinationAutoRedirect(destinations);

    if (_.get(settings, 'widget.ad.enabled')) {
      this.initAdSense();
    }

    this.applyCallToAction();
  }

  applyCallToAction() {
    const {callToAction} = this.props.record;

    if (callToAction) {
      const wrapper = document.createElement('span');
      this.callToActionContainer.appendChild(wrapper);
      injectHtml(wrapper, callToAction);
    }
  }

  getDestinationConfigs(record, device) {
    return _(record.destinations)
      .filter(d => d.isActive)
      .filter(d => !!d.url || d.kind === DestinationTypes.pollDestination)
      .filter(d => _.isEmpty(d.devices) || d.devices.indexOf(device) > -1)
      .uniqBy(d => d.name)
      .sortBy(destinationButtonsOrder)
      .value();
  }

  isCookieRedirectActive() {
    return !_.get(this.props, 'campaign.isCookieRedirectDisabled');
  }

  sendFrameRedirectCookieToParentWindow() {
    const {record, mode, isPreloaded} = this.props;
    if (this.isCookieRedirectActive() && isPreloaded && mode === cardLoadMode.iframe) {
      const cookie = Cookies.getJSON(RedirectConst.cookieName) || null;
      this.postRedirectStateToParentWindow(record.id, cookie);
    }
  }

  setupDestinationAutoRedirect(destinations) {
    const {mode} = this.props;

    if (mode === cardLoadMode.standalone) {
      const destination = _.find(destinations, {isAutoRedirect: true});

      if (destination) {
        const timeoutPeriod = (_.get(settings, 'widget.destinationRedirectTimeoutMS') || 4000) - 500;
        this.redirectTimeout = setTimeout(() => {
          this.onDestinationRedirectTimeout(destination);
        }, timeoutPeriod);
      }
    }
  }

  onDestinationRedirectTimeout(destination) {
    const {record, campaignId, setRedirectDestinationPreference} = this.props;

    setRedirectDestinationPreference(destination.name, destination.kind);

    if (this.isCookieRedirectActive()) {
      const cookie = Cookies.getJSON(RedirectConst.cookieName);
      this.postRedirectStateToParentWindow(record.id, cookie);
    }

    this.appendCommonDestinationClickTrackingCodes();
    this.appendDestinationClickTrackingCode(destination.name);

    ClientAnalytics.sendCardRedirect(
      this.getCardId(record),
      campaignId,
      RedirectConst.redirectActions.auto,
      RedirectConst.redirectTypes.cardAuto,
      destination.url,
      BrowserUtils.getDeviceType(),
      destination.name
    );

    this.redirect(destination.url);
  }

  redirect(redirectUrl) {
    setTimeout(() => {
      this.innerRedirect(redirectUrl);
    }, 1000);
  }

  /* Wrapper for 'window.location.replace' to make mocking / testing easier */
  innerRedirect(redirectUrl) {
    window.location.href = redirectUrl;
  }

  getProgramId(record) {
    return record.program ? record.program.id : record.id;
  }

  getCardId(record) {
    return record.program ? record.id : null;
  }

  initAdSense() {
    (window.adsbygoogle = window.adsbygoogle || []).push({});
  }

  render() {
    const {record, mode} = this.props;
    const {isMobile} = this.state;
    const mediaConfig = this.getMediaConfig();

    const isCardPreview = (mode === cardLoadMode.preview);

    if (!record) {
      return null;
    }

    let contentStyle = null;
    if (this.getBackgroundVideo() && !isMobile) {
      contentStyle = {backgroundColor: 'transparent', overflow: 'hidden'};
    } else if (this.hasBackgroundImage()) {
      contentStyle = {
        background: `url(${getImageUrl(record.backgroundImage)}) center center fixed`,
        backgroundSize: 'cover'
      };
    } else if (_.get(record, 'backgroundColor')) {
      contentStyle = {backgroundColor: record.backgroundColor};
    }

    return (
      <div className="vuCard__content"
           style={contentStyle}>
        {
          this.renderBackgroundVideo()
        }
        {
          this.renderAd()
        }
        <div className="vuCard__content__movie">
          {
            !record.hideTitle &&
            <div className="vuCard__content__movie__title">
              {record.title}
            </div>
          }
          {
            (mediaConfig.videoUrl || mediaConfig.videoImg) ?
              <div className="vuCard__content__movie__video">
                {this.renderPreviewVideo(mediaConfig)}
              </div>
              : null
          }
          {
            mediaConfig.posterUrl ?
              <div className="vuCard__content__movie__cover">
                <img src={mediaConfig.posterUrl} alt={`${record.title} Poster`}/>
              </div>
              : null
          }
          <div className="vuCard__content__line"/>
        </div>
        <div className="vuCard__content__select">
          {
            this.setCustomStylesForCta()
          }
          {
            this.renderCta()
          }
          {
            this.renderDestinations(record)
          }
        </div>
        {
          this.renderShare(record, isCardPreview)
        }
        {
          this.renderDestinationClickTrackingCodeContainer()
        }
        {
          this.renderAbout()
        }
      </div>);
  }

  renderBackgroundVideo() {
    const videoUrl = this.getBackgroundVideo();

    if (videoUrl && !this.state.isMobile) {
      return (
        <video className="vuCard__content__backgroundVideo"
               preload="metadata"
               style={{
                 height: 'auto',
                 width: 'auto',
                 minHeight: '100%',
                 minWidth: '100%',
                 position: 'absolute',
                 top: '50%',
                 left: '50%',
                 transform: 'translate(-50%, -50%)',
                 zIndex: -100,
                 transition: '1s opacity'
               }}
               src={videoUrl}
               playsinline
               autoPlay
               muted
               loop/>
      );
    }
  }

  hasBackgroundAsset() {
    return !!this.getBackgroundVideo() || this.hasBackgroundImage();
  }

  getBackgroundVideo() {
    const {record} = this.props;
    return record.backgroundVideo;
  }

  hasBackgroundImage() {
    const {record} = this.props;
    return !!getImageUrl(record.backgroundImage);
  }

  getProviderButtonStyle() {
    return this.hasBackgroundAsset() ? {backgroundColor: 'rgba(0, 0, 0, 0.5)'} : null;
  }

  setCustomStylesForCta() {
    const {callToActionColor} = this.props.record;

    if (callToActionColor) {
      return (
        <style dangerouslySetInnerHTML={{
          __html: `
.vuCard__content__select__cta {
  color: ${callToActionColor};
}
`
        }}/>
      );
    }
  }

  renderCta() {
    return (
      <div className="vuCard__content__select__cta"
           ref={(node) => {
             this.callToActionContainer = node;
           }}
      />);
  }

  getMediaConfig() {
    const {record, mode} = this.props;
    const posterUrl = CardUtils.getHorizontalPosterImageUrl(record);
    const videoUrl = CardUtils.getPosterVideoUrl(record);
    const videoImg = posterUrl;

    if (mode === cardLoadMode.preview && record.isVideoPreviewBlocked) {
      return {videoUrl, videoKind: 'upload', videoImg: _.get(settings, 'create.logoUrl', '/static/logo.png')};
    }

    return videoUrl ? {videoUrl, videoKind: record.posterVideo.kind, videoImg} : {posterUrl};
  }

  renderAd() {
    const {isVisible} = this.props;
    const {record, campaign} = this.props;
    let adStatus,
      adImageUrl,
      adUrl,
      renderedAd;

    if (!isVisible || !campaign) {
      return null;
    }

    const campaignAdStatus = _.get(campaign, 'adStatus', 'default');
    adStatus = (campaignAdStatus === 'default') ? record.defaultAdStatus : campaign.adStatus;
    adImageUrl = getImageUrl(campaign.adImage);
    adUrl = campaign.adUrl;

    if (adStatus !== 'show') {
      return null;
    }

    if (adImageUrl) {
      if (adUrl) {
        renderedAd = (
          <a href={adUrl}
             target="_blank"
             rel="noopener noreferrer"
             className="vuCard__content__ad__link">
            <img src={adImageUrl}
                 className="vuCard__content__ad__image-custom"
                 alt="advertisement"/>
          </a>
        );
      } else {
        renderedAd = (
          <img src={adImageUrl}
               className="vuCard__content__ad__image-custom"
               alt="advertisement"/>
        );
      }
    } else {
      renderedAd = (
        <div>
          {
            _.get(settings, 'widget.ad.enabled') ?
              <span>
                <script async src="//pagead2.googlesyndication.com/pagead/js/adsbygoogle.js"/>
                <ins className="adsbygoogle"
                     data__ad-client={settings.widget.ad.client}
                     data__ad-slot={settings.widget.ad.slot}
                     data__ad-format="auto"/>
              </span>
              : null
          }
          {
            this.renderStaticAd()
          }
        </div>
      );
    }

    return (
      (adStatus === 'show') ?
        <div className="vuCard__content__ad">
          {renderedAd}
        </div>
        : null
    );
  }

  renderStaticAd() {
    const {ad} = this.props;

    if (!ad) {
      return null;
    }

    return (
      <a href={ad.url}
         target="_blank"
         rel="noopener noreferrer"
         className="vuCard__content__ad__link">
        <img src={ad.imageUrl}
             alt="advertisement"/>
      </a>);
  }

  renderPreviewVideo(config) {
    const {isVisible} = this.props;

    if (!isVisible) return null;

    return (
      <EmbedVideo url={config.videoUrl}
                  kind={config.videoKind}
                  poster={config.videoImg}/>);
  }

  renderDestinations() {
    if (!this.props.isVisible) {
      return null;
    }

    const destinationButtons = _.map(this.state.destinations, (d, index) => (
      this.renderSingleDestination(d, `destination-${index}`)
    ));

    return (
      <div className="vuCard__content__select__publishers">
        {destinationButtons}
      </div>
    );
  }

  renderSingleDestination(destination, key) {
    const logoUrl = CardDestinationUtils.getLogoImageUrl(destination);

    const iconStyle = {background: `url(${logoUrl}) center center`};
    const linkProps = {href: destination.url, title: destination.name};

    const url = urlParse(destination.url, true);
    if (url.protocol !== 'tel:') {
      linkProps.target = '_blank';
    }

    return (
      <div key={key}
           style={this.getProviderButtonStyle()}
           className={`vuCard__content__select__publisher vuCard__content__select__publisher--${destination.kind}`}>
        <a {...linkProps}
           data-name={destination.name}
           onClick={this.onDestinationClick.bind(this, destination.name, destination.url, destination.kind)}>
          <div className="vuCard__content__select__publisher__icon--destination"
               style={iconStyle}/>
        </a>
      </div>);
  }

  onDestinationClick(name, url, kind) {
    const {mode, record, campaignId, collectionId, rendererId, setRedirectDestinationPreference} = this.props;

    this.cancelRedirectionTimer();
    setRedirectDestinationPreference(name, kind);

    if (mode === cardLoadMode.iframe && this.isCookieRedirectActive()) {
      const cookie = Cookies.getJSON(RedirectConst.cookieName);
      this.postRedirectStateToParentWindow(record.id, cookie);
    }

    this.appendCommonDestinationClickTrackingCodes();
    this.appendDestinationClickTrackingCode(name);

    ClientAnalytics.sendCardDestinationClicked(
      name,
      CardClickTypes.provider,
      url,
      this.getProgramId(record),
      record.title,
      rendererId,
      this.getCardId(record),
      campaignId,
      collectionId);
  }

  cancelRedirectionTimer() {
    if (this.redirectTimeout) {
      clearTimeout(this.redirectTimeout);
    }
  }

  postRedirectStateToParentWindow(cardId, cookie) {
    window.parent.postMessage({
      title: FrameMessages.setRedirectState,
      cardId,
      cookie
    }, '*');
  }

  appendDestinationClickTrackingCode(destinationName) {
    const {record} = this.props;
    const destinationConfig = _.find(record.destinations, {name: destinationName});
    const {trackingCode} = destinationConfig;

    if (trackingCode) {
      const wrapper = document.createElement('span');
      this.providerTrackingCodeContainer.appendChild(wrapper);
      injectHtml(wrapper, trackingCode);
    }
  }

  /* Legacy tracking code logic / no distinction over destination name */
  appendCommonDestinationClickTrackingCodes() {
    const {record} = this.props;

    let codes = '';
    record.trackingCodes.forEach((codeConfig) => {
        const code = CardUtils.getTrackingCode(codeConfig, this.state.device, 'destination_click');
        if (code) {
          codes += code;
        }
      }
    );

    if (codes) {
      const wrapper = document.createElement('span');
      this.providerTrackingCodeContainer.appendChild(wrapper);
      injectHtml(wrapper, codes);
    }
  }

  renderShare(record, isCardPreview) {
    if (!this.props.isVisible) {
      return null;
    }
    const {isMobile} = this.state;

    return (
      <div className="vuCard__content__share">
        <div className="vuCard__content__share__btn-panel">
          <a className="vuCard__content__share__btn twitter"
             onClick={this.onShareClick.bind(this, this.getTwitterShareUrl(`${record.title}`),
               'twitter',
               isCardPreview
             )}
             title="twitter">
            <i className="fa fa-twitter"/>
          </a>
        </div>
        <div className="vuCard__content__share__btn-panel">
          <a className="vuCard__content__share__btn facebook"
             onClick={this.onShareClick.bind(this, this.getFbShareUrl(), 'facebook', isCardPreview)}
             title="facebook">
            <i className="fa fa-facebook"/>
          </a>
        </div>
        <div className="vuCard__content__share__btn-panel">
          <a className="vuCard__content__share__btn google-plus"
             onClick={this.onShareClick.bind(this, this.getGooglePlusShareUrl(), 'google-plus', isCardPreview)}
             title="google plus">
            <i className="fa fa-google-plus"/>
          </a>
        </div>
        <div className="vuCard__content__share__btn-panel">
          <a className="vuCard__content__share__btn pinterest"
             onClick={this.onShareClick.bind(this, this.getPinterestShareUrl(record), 'pinterest', isCardPreview)}
             title="share"
             data-pin-do="buttonPin">
            <i className="fa fa-pinterest-p"/>
          </a>
        </div>
        <div className="vuCard__content__share__btn-panel">
          <a className="vuCard__content__share__btn linkedin"
             onClick={this.onShareClick.bind(this, this.getLinkedinShareUrl(record.title), 'linkedin', isCardPreview)}
             title="share">
            <i className="fa fa-linkedin"/>
          </a>
        </div>
        <div className="vuCard__content__share__btn-panel">
          <a href={isMobile ? this.getEmailShareUrl(record.title, isCardPreview) : '#'}
             className="vuCard__content__share__btn email"
             onClick={isMobile ? null : this.onShareClick.bind(this, this.getEmailShareUrl(record.title), 'email', isCardPreview)}
             title="share">
            <i className="fa fa-envelope-o"/>
          </a>
        </div>
        <div className="vuCard__content__share__btn-panel">
          <a className="vuCard__content__share__btn tumblr"
             onClick={this.onShareClick.bind(this, this.getTumblrShareUrl(record), 'tumblr', isCardPreview)}
             title="share">
            <i className="fa fa-tumblr"/>
          </a>
        </div>
      </div>
    );
  }

  onShareClick(shareUrl, media, isDisabled = false) {
    if (isDisabled) {
      return false;
    }

    if (!this.shareWinRef || this.shareWinRef.closed) {
      //create new
      this.shareWinRef = window.open(shareUrl, 'sharewin', 'left=20,top=20,width=500,height=400,toolbar=1,resizable=0');
    } else {
      this.shareWinRef.location = shareUrl;
      this.shareWinRef.focus();
    }

    const {record, campaignId, collectionId, rendererId} = this.props;
    ClientAnalytics.sendCardShared(media, shareUrl, this.getProgramId(record), record.title, rendererId, this.getCardId(record), campaignId, collectionId);
  }

  getPinterestShareUrl(record) {
    const description = encodeURIComponent(`Don't miss ${record.title}! Just click on ${this.props.shareUrl}`);
    return (`//www.pinterest.com/pin/create/button/?url=${this.props.shareUrl}&media=${record.posterUrl}&description=${description}`);
  }

  getTwitterShareUrl(title) {
    const prefix = 'Don\'t miss ';
    const suffix = '!';
    const ellipses = '...';
    const hashtags = ['VuPulse'].join(',');
    const MAX_TWEET_LENGTH = 280;
    const maxTitleLength = MAX_TWEET_LENGTH - this.props.shareUrl.length - 1 - prefix.length - suffix.length - ellipses.length - hashtags.length;
    let tweetText = title.substring(0, maxTitleLength) + (title.length > maxTitleLength ? ellipses : '');
    tweetText = prefix + tweetText + suffix;
    return (`//twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}&hashtags=${hashtags}&url=${this.props.shareUrl}`);
  }

  getFbShareUrl() {
    return ('//www.facebook.com/sharer/sharer.php?u=' + this.props.shareUrl);
  }

  getGooglePlusShareUrl() {
    return ('//plus.google.com/share?url=' + this.props.shareUrl);
  }

  getLinkedinShareUrl(title) {
    const summary = encodeURIComponent(`Don't miss ${title}! Just click on ${this.props.shareUrl}`);
    const urlTitle = encodeURIComponent(title);
    return (`https://www.linkedin.com/shareArticle?mini=true&url=${this.props.shareUrl}&title=${urlTitle}&summary=${summary}`);
  }

  getEmailShareUrl(title, isDisabled) {
    const urlTitle = encodeURIComponent(title);
    if (isDisabled) {
      return null;
    }
    return (`mailto:?subject=Don't miss ${urlTitle}!&body=Just click on ` + this.props.shareUrl);
  }

  getTumblrShareUrl(record) {
    const urlTitle = encodeURIComponent(record.title);
    return (`https://tumblr.com/share/?title=${urlTitle}&tags=VuPulse&content=${record.posterUrl}&caption=Never miss an episode of ${urlTitle}! record, watch, and be reminded on ${this.props.shareUrl}&show-via=VuPulse&canonicalUrl=${this.props.shareUrl}`);
  }

  renderAbout() {
    return (
      <a className="vuCard__content__about"
         href="https://vupulse.com/"
         target="_blank"
         rel="noopener noreferrer">
        About VuPulse
      </a>
    );
  }

  renderDestinationClickTrackingCodeContainer() {
    return (
      <span className="vuCard__destinationTrackingCode"
            style={{position: 'absolute', bottom: '0'}}
            ref={(node) => {
              this.providerTrackingCodeContainer = node;
            }}
      />
    );
  }
}

const destinationButtonsOrder = item => (_.isNaN(item.order) ? Number.MAX_VALUE : item.order);

VuCardClassic.propTypes = {
  shareUrl: PropTypes.string,
  record: PropTypes.object.isRequired,
  campaign: PropTypes.object.isRequired,
  ad: PropTypes.object,
  mode: PropTypes.oneOf(Object.values(cardLoadMode)),
  rendererId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  renderer: PropTypes.object,
  isPreloaded: PropTypes.bool.isRequired,
  setRedirectDestinationPreference: PropTypes.func.isRequired
};

VuCardClassic.defaultProps = {
  shareUrl: '',
  cardStyle: cardStyles.classic,
  isPreloaded: false,
  setRedirectDestinationPreference: () => {},
};
